import React, { useEffect, useState } from 'react';
import { ShoppingBag, Sparkles, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSapphires } from '../context/SapphireContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import EmailMatchWarning from '../components/EmailMatchWarning';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACKS = [
  { id: 'p1', name: 'Apprentice Pouch', amount: 500, price: 4.99, popular: false },
  { id: 'p2', name: 'Adventurer Chest', amount: 1200, price: 9.99, popular: true },
  { id: 'p3', name: 'Knight Coffer', amount: 2800, price: 19.99, popular: false },
  { id: 'p4', name: 'Royal Vault', amount: 7500, price: 49.99, popular: false },
];

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // ~60s

const PaymentResult = ({ result, onClose }) => {
  if (!result) return null;
  const {
    status,
    payment_status,
    sapphires_added,
    account_level,
    error,
    test_mode,
    pack,
    firestore,
    target_email,
    web_balance,
  } = result;
  if (test_mode) {
    const matched = firestore && firestore.matched;
    return (
      <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 fade-up">
        <div className="relative w-full max-w-lg rounded-2xl border border-[#f5b400]/40 bg-gradient-to-br from-[#1a1410] via-black to-black p-7">
          <div className="text-center">
            {matched ? (
              <CheckCircle2 size={56} className="text-[#f5b400] mx-auto" />
            ) : (
              <AlertTriangle size={48} className="text-amber-400 mx-auto" />
            )}
            <p className="font-nav text-[10px] tracking-widest text-amber-400 mt-3">
              ⚠ TEST MODE — NO REAL PAYMENT
            </p>
            <h3 className="font-heading text-white text-2xl mt-2">
              {matched ? 'Sapphires credited!' : 'Web balance updated'}
            </h3>
            <p className="font-body text-white/75 text-sm mt-2">
              <b>{(pack?.sapphires || 0).toLocaleString()}</b> Sapphires from{' '}
              <i>{pack?.name}</i>
            </p>
          </div>
          <div className="mt-5 rounded-lg bg-black/50 border border-white/10 p-4 text-left font-body text-sm space-y-2">
            <div className="flex justify-between text-white/80">
              <span>Target email</span>
              <code className="font-mono text-[#f5b400] text-xs">{target_email}</code>
            </div>
            <div className="flex justify-between text-white/80">
              <span>Web balance</span>
              <span className="text-white">
                {web_balance?.new_balance?.toLocaleString() || '—'}{' '}
                <span className="text-white/40">(lvl {web_balance?.account_level || '—'})</span>
              </span>
            </div>
            <div className="border-t border-white/10 my-2" />
            <div className="flex justify-between items-center">
              <span className="text-white/80">Firestore match</span>
              <span
                className={`font-nav text-[10px] tracking-widest px-2 py-1 rounded ${
                  matched
                    ? 'bg-[#f5b400] text-black'
                    : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                }`}
              >
                {matched ? 'MATCHED ✓' : 'NO MATCH'}
              </span>
            </div>
            {matched ? (
              <>
                <div className="flex justify-between text-white/80">
                  <span>Player doc</span>
                  <code className="font-mono text-[#f5b400] text-[11px] truncate max-w-[260px]">
                    {firestore.doc_id}
                  </code>
                </div>
                <div className="flex justify-between text-white/80">
                  <span>Sapphire before</span>
                  <span className="text-white">{(firestore.previous ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-white">
                  <span>Sapphire after</span>
                  <b className="text-[#f5b400]">
                    {(firestore.new_total ?? 0).toLocaleString()}
                  </b>
                </div>
              </>
            ) : (
              <p className="text-amber-300/90 text-xs leading-relaxed pt-1">
                No in-game player was found with this email. An admin alert email
                has been sent so the team can credit the player manually.
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="mt-6 w-full bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs py-3 rounded-md transition-colors"
            data-testid="test-purchase-close"
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  }
  let icon, title, body;
  if (error) {
    icon = <AlertTriangle size={48} className="text-red-400" />;
    title = 'Payment check failed';
    body = error;
  } else if (payment_status === 'paid') {
    icon = <CheckCircle2 size={56} className="text-[#f5b400]" />;
    title = 'Payment successful!';
    body = sapphires_added
      ? `${sapphires_added.toLocaleString()} Sapphires were added to your bag. Account level: ${account_level}.`
      : 'Thank you for your purchase!';
  } else if (status === 'expired') {
    icon = <AlertTriangle size={48} className="text-red-400" />;
    title = 'Session expired';
    body = 'This checkout session expired before payment was completed. Please try again.';
  } else {
    icon = <div className="w-12 h-12 mx-auto border-2 border-[#f5b400] border-t-transparent rounded-full animate-spin" />;
    title = 'Payment processing';
    body = 'Your payment is still processing. Refresh the page in a minute or check your email.';
  }
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 fade-up">
      <div className="relative w-full max-w-md rounded-2xl border border-[#f5b400]/40 bg-gradient-to-br from-[#1a1410] via-black to-black p-7 text-center">
        <div className="mx-auto">{icon}</div>
        <h3 className="font-heading text-white text-2xl mt-4">{title}</h3>
        <p className="font-body text-white/75 text-sm mt-3">{body}</p>
        <button
          onClick={onClose}
          className="mt-6 bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs px-6 py-3 rounded-md transition-colors"
        >
          CONTINUE
        </button>
      </div>
    </div>
  );
};

const StorePage = () => {
  const { sapphires, setSapphires } = useSapphires();
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loadingPack, setLoadingPack] = useState(null);
  const [result, setResult] = useState(null);
  // Hidden dev toggle so the team can still validate the in-game credit flow
  // while the storefront is locked to the public. Visit `/store?dev=1` once on
  // a browser to enable; `/store?dev=0` to disable.
  const [devMode, setDevMode] = useState(false);
  useEffect(() => {
    try {
      const flag = params.get('dev');
      if (flag === '1') {
        localStorage.setItem('nro_dev', '1');
        params.delete('dev');
        setParams(params, { replace: true });
      } else if (flag === '0') {
        localStorage.removeItem('nro_dev');
        params.delete('dev');
        setParams(params, { replace: true });
      }
      setDevMode(localStorage.getItem('nro_dev') === '1');
    } catch (_) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect return-from-Stripe and poll for status
  useEffect(() => {
    const success = params.get('success');
    const canceled = params.get('canceled');
    const sessionId = params.get('session_id');
    if (canceled) {
      toast({ title: 'Payment canceled', description: 'No charge was made.' });
      setParams({}, { replace: true });
      return;
    }
    if (success && sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await axios.get(`${API}/checkout/status/${encodeURIComponent(sessionId)}`);
          if (data.payment_status === 'paid' || data.status === 'expired') {
            setResult(data);
            if (data.payment_status === 'paid') {
              if (typeof data.new_balance === 'number' && setSapphires) {
                setSapphires(data.new_balance);
              }
              refresh && refresh();
            }
            setParams({}, { replace: true });
            return;
          }
          if (attempts++ < MAX_POLLS) {
            setTimeout(poll, POLL_INTERVAL_MS);
          } else {
            setResult({ ...data });
            setParams({}, { replace: true });
          }
        } catch (e) {
          setResult({ error: e.response?.data?.detail || e.message || 'Could not verify payment.' });
          setParams({}, { replace: true });
        }
      };
      setResult({ payment_status: 'processing', status: 'open' });
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (pack) => {
    if (!devMode) return;
    if (!user) {
      navigate('/account');
      return;
    }
    setLoadingPack(pack.id);
    try {
      const { data } = await axios.post(
        `${API}/checkout/sapphires-test`,
        { pack_id: pack.id },
        { headers: { 'X-Dev-Mode': '1' } }
      );
      setResult(data);
      if (data?.web_balance?.new_balance != null) {
        setSapphires(data.web_balance.new_balance);
      }
      try { refresh && refresh(); } catch (_) {}
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Purchase failed.';
      toast({ title: 'Purchase failed', description: msg, variant: 'destructive' });
    } finally {
      setLoadingPack(null);
    }
  };

  return (
    <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-12 text-center fade-up">
          <p className="font-nav text-[#f5b400] text-xs tracking-widest">STORE</p>
          <h1 className="font-heading section-heading-white mt-3">THE SAPPHIRE</h1>
          <h1 className="font-heading section-heading-gold">STORE</h1>
          <p className="mt-4 text-white/75 font-body max-w-2xl mx-auto">
            Power up your journey with premium Sapphires — the in-game currency of Eldoria.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-black/60 border border-[#f5b400]/40 px-4 py-2 rounded-full">
            <Sparkles className="text-[#f5b400]" size={16} />
            <span className="font-nav text-xs text-white">
              CURRENT BALANCE: <span className="text-[#f5b400]">{sapphires.toLocaleString()}</span> SAPPHIRES
            </span>
          </div>
        </div>

        {!user && (
          <div className="max-w-2xl mx-auto mb-8 p-4 rounded-xl border border-[#f5b400]/30 bg-[#f5b400]/5 text-center">
            <p className="font-body text-white/85 text-sm">
              <Lock size={14} className="inline -mt-1 mr-2 text-[#f5b400]" />
              You need an account to purchase Sapphires.{' '}
              <Link to="/account" className="text-[#f5b400] hover:underline font-nav text-xs">
                SIGN IN
              </Link>
            </p>
          </div>
        )}

        <div className="max-w-2xl mx-auto">
          {!devMode && (
            <div
              className="mb-6 rounded-xl border-2 border-amber-500/70 bg-gradient-to-br from-amber-500/10 via-[#1a1410]/70 to-black/70 p-5 md:p-6"
              role="alert"
              data-testid="store-paused-banner"
            >
              <div className="flex gap-4 items-start">
                <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={26} />
                <div>
                  <h3 className="font-heading text-amber-300 text-lg md:text-xl uppercase tracking-wide">
                    Sapphires temporarily unavailable
                  </h3>
                  <p className="text-white/85 text-sm mt-1.5 leading-relaxed">
                    Sapphire pack purchases are <b>paused</b> while we finish setting
                    things up for launch. Please check back soon.
                  </p>
                  <p className="text-white/75 text-sm mt-2 leading-relaxed">
                    In the meantime, if you would like to support the project, the{' '}
                    <a
                      href="/donate"
                      className="text-amber-300 underline hover:no-underline font-semibold"
                    >
                      Donate
                    </a>{' '}
                    page is fully open and every contribution helps keep Eldoria alive.
                  </p>
                </div>
              </div>
            </div>
          )}
          <EmailMatchWarning />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PACKS.map((p) => (
            <div
              key={p.id}
              className={`relative rounded-xl border p-6 flex flex-col items-center text-center transition-all duration-300 ${
                p.popular
                  ? 'border-[#f5b400] bg-black/60 shadow-[0_0_30px_-10px_rgba(245,180,0,0.5)]'
                  : 'border-white/15 bg-black/40 hover:border-[#f5b400]/60'
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#f5b400] text-black font-nav text-[10px] px-3 py-1 rounded-full">
                  BEST VALUE
                </span>
              )}
              <Sparkles className="text-[#f5b400] mb-3" size={28} />
              <h4 className="font-heading text-white text-xl">{p.name}</h4>
              <p className="font-heading text-3xl text-[#f5b400] mt-3">{p.amount.toLocaleString()}</p>
              <p className="text-white/60 text-xs font-body">Sapphires</p>
              <button
                onClick={() => buy(p)}
                disabled={!devMode || loadingPack === p.id}
                data-testid={`buy-${p.id}`}
                className="mt-5 w-full bg-[#f5b400] hover:bg-[#ffc630] disabled:opacity-50 disabled:cursor-not-allowed text-black font-nav text-xs py-3 rounded-md flex items-center justify-center gap-2 transition-colors"
              >
                <ShoppingBag size={16} />
                {!devMode
                  ? 'COMING SOON'
                  : loadingPack === p.id
                  ? 'CREDITING…'
                  : `BUY — €${p.price.toFixed(2)}`}
              </button>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-white/45 text-xs font-body">
          Secure payments by Stripe · all prices in EUR
        </p>
      </div>

      <PaymentResult result={result} onClose={() => setResult(null)} />
    </div>
  );
};

export default StorePage;
